import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  LAYOUT_SYNC_DEBOUNCE_MS,
  isLayoutMessage,
  type Layer,
  type ProgramAudio,
  type StateUpdateMessage,
} from '@liveforge/shared';
import { idToken, relayOrigin } from '../lib/api';

/**
 * Publishes the host's layer tree to every Layout Page rendering this Live.
 *
 * The host authenticates with its Firebase ID token; the relay checks it owns
 * the Live before accepting any state. A Layout Page connects to the same
 * channel as an unauthenticated viewer and can only receive.
 */
export function useLayoutPublisher(
  liveId: string | undefined,
  layers: Layer[],
  audio: ProgramAudio,
  revision: number,
) {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!liveId) return;

    let socket: Socket | undefined;
    let cancelled = false;

    void (async () => {
      let token: string;
      try {
        token = await idToken();
      } catch {
        setError('Could not read your session; the layout preview will not sync.');
        return;
      }
      if (cancelled) return;

      socket = io(relayOrigin(), {
        path: '/socket.io',
        auth: { liveId, token, role: 'host' },
        transports: ['websocket', 'polling'],
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        setConnected(true);
        setError(null);
      });
      socket.on('disconnect', () => setConnected(false));
      socket.on('connect_error', (cause: Error) => setError(cause.message));

      // The relay answers a Layout Page's request itself, from the last state
      // it saw. Nothing is expected back here; the listener exists so a stray
      // message cannot go unhandled.
      socket.on('layout', (payload: unknown) => {
        if (!isLayoutMessage(payload)) return;
      });
    })();

    return () => {
      cancelled = true;
      socket?.close();
      socketRef.current = null;
      setConnected(false);
    };
  }, [liveId]);

  /*
   * Dragging a slider produces a state change per pixel. Sending each one
   * would flood the relay with trees the renderer never gets to draw, so they
   * collapse into one send per frame-ish window.
   *
   * **`connected` is a dependency, and that is the point.** This effect also
   * runs on mount, ~50ms before a socket that has to fetch a Firebase token
   * first can possibly be up - so the first send was always dropped by the
   * guard below, and nothing was published until the director happened to
   * change something. The relay held no state, so a Layout Page connecting
   * afterwards asked for the composition, was answered with nothing, and drew
   * a black frame. That is what a freshly opened studio looked like to Egress.
   */
  useEffect(() => {
    if (!liveId || !connected) return;

    const timer = setTimeout(() => {
      const socket = socketRef.current;
      if (!socket?.connected) return;

      const message: StateUpdateMessage = {
        type: 'state_update',
        liveId,
        state: { layers, audio, revision },
      };
      socket.emit('layout', message);
    }, LAYOUT_SYNC_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [liveId, connected, layers, audio, revision]);

  return { connected, error };
}
